import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  Button,
  SpaceBetween,
  Box,
  ColumnLayout,
  TextContent,
  Cards,
  Badge,
  Alert,
  Icon,
  Hotspot,
} from '@cloudscape-design/components';
import { useVersion } from '../context/VersionContext';
import { useTheme } from '../context/ThemeContext';
import { HOTSPOT_IDS } from '../context/TutorialContext';
import { useFlowStep } from '../hooks/useAnalytics';

function HomePage() {
  const navigate = useNavigate();
  const { showV2 } = useVersion();
  const { isDark } = useTheme();

  // The black-on-white "typeface_black_icon" logo renders poorly on a dark
  // background (white card with black text). When dark mode is active we
  // swap in the colour/blue transparent-background version which reads
  // cleanly on both light and dark surfaces.
  const logoSrc = isDark
    ? '/images/logo/typeface_colour_blue_transparent.png'
    : '/images/logo/typeface_black_icon.png';

  const handleBeginAnalysis = () => {
    if (showV2) {
      navigate('/v2/process');
    } else {
      navigate('/upload');
    }
  };

  const handlePersonaSelect = (persona) => {
    navigate('/v2/process');
  };

  const personas = [
    {
      id: 'sara-chen',
      name: 'Sara Chen',
      jobTitle: 'Senior Investment Analyst',
      jobFunction: 'Investment Research & Analysis',
      task: 'Quarterly portfolio review and risk assessment',
      avatar: '/avatars/financial-advisor.jpg',
      currentProcess: 'Manually reviews 200+ pages of reports across multiple documents of unstructured data, extracts key financial metrics, identifies risk factors, and compiles investment recommendations. Process takes 3-4 hours per company.',
      aiOpportunity: 'AI can extract key insights in minutes, allowing Sara to focus on strategic analysis and client recommendations rather than data gathering.',
      buttonText: 'Experience Sara\'s Workflow',
      buttonVariant: 'primary'
    },
    {
      id: 'generic-analyst',
      name: 'Financial Analyst',
      jobTitle: 'Financial Analyst',
      jobFunction: 'Research & Analysis',
      task: 'Document analysis and insight extraction',
      initials: '?',
      currentProcess: 'Spends significant time manually analyzing financial documents, extracting data, and preparing reports for decision-making processes.',
      aiOpportunity: 'AI-powered analysis can accelerate research workflows, improve accuracy, and free up time for higher-value strategic work.',
      buttonText: 'Explore This Workflow',
      buttonVariant: 'normal',
      isGeneric: true,
      question: 'Can you think of a profile in your business that would benefit from such a workflow or currently performs analysis/research functions?'
    }
  ];

  return (
    <Container>
      <SpaceBetween size="l">
        <Box textAlign="center">
          <img 
            src={logoSrc}
            alt="ResearchIQ"
            style={{ height: '70px', width: 'auto', marginBottom: '12px' }}
          />
          <TextContent>
            <p style={{ fontSize: '16px', maxWidth: '600px', margin: '0 auto', textAlign: 'center', opacity: 0.8 }}>
              An <strong>advanced financial research tool</strong> that helps you analyze
              financial documents, extract key insights, and generate comprehensive reports.
              <Hotspot hotspotId={HOTSPOT_IDS.homeBeginAnalysis} direction="bottom" />
            </p>
          </TextContent>
          <Box margin={{ top: "l" }}>
            <Button variant="primary" onClick={handleBeginAnalysis}>
              Begin Analysis
            </Button>
          </Box>
        </Box>

        <Header variant="h2">Analyst Personas</Header>
        
        <Cards
          cardDefinition={{
            header: item => item.name,
            sections: [
              {
                id: 'avatar',
                content: item => (
                  <ColumnLayout columns={[4, 6]} variant="text-grid">
                    <div>
                      {item.avatar ? (
                        <img 
                          src={item.avatar} 
                          alt={`${item.name}'s avatar`} 
                          style={{ 
                            width: '100px', 
                            height: '100px', 
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '2px solid #eaeded'
                          }} 
                        />
                      ) : (
                        <div style={{
                          width: '100px',
                          height: '100px',
                          borderRadius: '50%',
                          backgroundColor: '#687078',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          fontWeight: 'bold'
                        }}>
                          {item.initials}
                        </div>
                      )}
                    </div>
                    <div>
                      <SpaceBetween size="xs">
                        {item.isGeneric ? (
                          <Badge color="grey">{item.jobTitle}</Badge>
                        ) : (
                          <Badge color="blue">{item.jobTitle}</Badge>
                        )}
                        <div style={{ fontSize: '14px', opacity: 0.8 }}>
                          <strong>Function:</strong> {item.jobFunction}
                        </div>
                        <div style={{ fontSize: '14px', opacity: 0.8 }}>
                          <strong>Task:</strong> {item.task}
                        </div>
                      </SpaceBetween>
                    </div>
                  </ColumnLayout>
                )
              },
              {
                id: 'process',
                content: item => (
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      color: item.isGeneric ? 'inherit' : 'inherit',
                      opacity: item.isGeneric ? 0.6 : 1,
                      marginBottom: '8px',
                      fontWeight: 'bold'
                    }}>
                      <Icon name="list-view" variant="subtle" />
                      Current Process
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      opacity: item.isGeneric ? 0.5 : 0.8, 
                      maxWidth: '80%' 
                    }}>
                      {item.currentProcess}
                      {item.id === 'sara-chen' && (
                        <Hotspot
                          hotspotId={HOTSPOT_IDS.homeCurrentProcess}
                          direction="right"
                        />
                      )}
                    </div>
                  </div>
                )
              },
              {
                id: 'opportunity',
                content: item => (
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      color: item.isGeneric ? 'inherit' : 'inherit',
                      opacity: item.isGeneric ? 0.6 : 1,
                      marginBottom: '8px',
                      fontWeight: 'bold'
                    }}>
                      <Icon name="gen-ai" variant={item.isGeneric ? "subtle" : "success"} />
                      AI Opportunity
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      opacity: item.isGeneric ? 0.5 : 0.9, 
                      fontWeight: '500', 
                      maxWidth: '80%' 
                    }}>
                      {item.id === 'sara-chen' ? (
                        <>
                          AI can <u>extract key insights in minutes</u>, allowing Sara to focus on strategic analysis and client recommendations rather than data gathering.
                        </>
                      ) : (
                        item.aiOpportunity
                      )}
                      <ul style={{ marginTop: '12px', paddingLeft: '20px' }}>
                        {item.isGeneric ? (
                          <>
                            <li>Parallel processing using agentic systems for comprehensive question answering.</li>
                            <li>Key insight extraction to automatically extract metrics, trends, comparisons and insights.</li>
                            <li>Comprehensive reports with formatting and customizable structure outputs.</li>
                            <li>AI-Powered Research to perform deep analysis and identify patterns.</li>
                          </>
                        ) : (
                          <>
                            <li>Enhanced AI processing with <strong>multi-agent processing</strong>, real-time logs, and comprehensive question answering.</li>
                            <li><strong>Key insights extraction</strong> to automatically extract financial metrics, trends, and insights.</li>
                            <li><strong>Comprehensive</strong> reports with visualizations and <strong>summaries of financial data.</strong></li>
                            <li><strong>AI-powered research</strong> to perform <strong>deep analysis</strong> and identify patterns.</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                )
              },
              {
                id: 'question',
                content: item => item.isGeneric ? (
                  <Alert
                    statusIconAriaLabel="Info"
                    header="Discussion Prompt"
                  >
                    Can you think of a profile in your business that would benefit from such a workflow or currently performs analysis/research functions?
                  </Alert>
                ) : null
              },
              {
                id: 'action',
                content: item => !item.isGeneric ? (
                  <div style={{ position: 'relative' }}>
                    <Button 
                      variant={item.buttonVariant}
                      onClick={() => handlePersonaSelect(item)}
                      fullWidth
                    >
                      {item.buttonText}
                    </Button>
                    <Hotspot
                      hotspotId={HOTSPOT_IDS.homeSaraWorkflow}
                      direction="top"
                    />
                  </div>
                ) : null
              }
            ]
          }}
          cardsPerRow={[
            { cards: 1 },
            { minWidth: 500, cards: 2 }
          ]}
          items={personas}
          trackBy="id"
        />
      </SpaceBetween>
    </Container>
  );
}

export default HomePage;
